import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RagBadge } from "@/components/common/RagBadge";
import { LinkChip } from "@/components/workspace/ActionLinkPicker";
import { EvidencePanel, HistoryTimeline } from "@/components/workspace/WorkItemParts";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate, ACTION_STATUS } from "@/lib/format";
import { toast } from "sonner";

const GOLD = "bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]";

export function WorkItemDialog({ actionId, onClose, onChanged }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(null);
  const [text, setText] = useState("");
  const [mode, setMode] = useState(null); // blocker | submit | return
  const load = () => api.get(`/workspace/actions/${actionId}`).then((r) => setA(r.data))
    .catch((e) => { toast.error(apiError(e.response?.data?.detail)); onClose(); });
  useEffect(() => { load(); }, [actionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); setMode(null); setText(""); await load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!a) return null;
  const role = a.my_role;
  const doer = role === "owner" || role === "manager";
  const closed = ["completed", "cancelled"].includes(a.status);
  const awaiting = a.status === "awaiting_review";
  const st = ACTION_STATUS[a.status] || ACTION_STATUS.not_started;
  const iAmReviewer = role === "reviewer" && awaiting;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="work-item-dialog">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <RagBadge rag={st.rag} testId="work-status">{st.label}</RagBadge>
            <LinkChip a={a} />
            {a.requires_review && <span className="inline-flex items-center gap-1 text-xs text-[#B87F1E]"><Icons.ShieldCheck size={13} /> Reviewed by {a.reviewer_name || "—"}</span>}
          </div>
          <DialogTitle className="font-display text-xl">{a.title}</DialogTitle>
          <DialogDescription>
            {a.owner_name} · due {fmtDate(a.due_date)}{a.cadence && a.cadence !== "one_off" ? ` · ${a.cadence} responsibility` : ""}
          </DialogDescription>
        </DialogHeader>

        {a.description && <p className="text-sm text-[#1E2D44]/75">{a.description}</p>}
        {a.status === "blocked" && (
          <div className="flex items-start gap-2 rounded-lg bg-[#C95555]/8 px-3.5 py-3 text-sm text-[#C95555]" data-testid="work-blocker">
            <Icons.OctagonAlert size={16} className="mt-0.5 shrink-0" /><div><b>Blocked:</b> {a.blocker}</div>
          </div>
        )}
        {a.review_status === "returned" && !awaiting && (
          <div className="rounded-lg bg-[#D99A32]/12 px-3.5 py-3 text-sm text-[#8a5f17]" data-testid="work-returned">
            <b>Returned for changes:</b> {a.review_notes}
          </div>
        )}

        {doer && !closed && !awaiting && (
          <div className="rounded-xl border border-[#E4E8EF] p-4" data-testid="work-controls">
            <div className="mb-2 flex items-center justify-between text-sm"><span className="text-[#1E2D44]/60">Progress</span><b>{a.percent_complete || 0}%</b></div>
            <div className="flex flex-wrap gap-1.5">
              {[0, 25, 50, 75].map((p) => (
                <Button key={p} size="sm" variant={a.percent_complete === p ? "default" : "outline"} className="h-8"
                  disabled={!!busy || a.status === "blocked"} data-testid={`progress-${p}`}
                  onClick={() => run("p", () => api.post(`/workspace/actions/${a.id}/progress`, { status: p ? "in_progress" : "not_started", percent_complete: p }), "Progress saved")}>
                  {p}%
                </Button>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {a.status === "blocked" ? (
                <Button variant="outline" disabled={!!busy} data-testid="clear-blocker-btn"
                  onClick={() => run("b", () => api.delete(`/workspace/actions/${a.id}/blocker`), "Blocker cleared")}>
                  <Icons.CircleCheck size={15} className="mr-1.5" /> Clear blocker
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setMode(mode === "blocker" ? null : "blocker")} data-testid="raise-blocker-btn">
                  <Icons.OctagonAlert size={15} className="mr-1.5" /> Raise blocker
                </Button>
              )}
              {role === "owner" && a.status !== "blocked" && (
                <Button className={GOLD} onClick={() => setMode(mode === "submit" ? null : "submit")} data-testid="submit-work-btn">
                  <Icons.Send size={15} className="mr-1.5" /> {a.requires_review ? "Submit for review" : "Mark complete"}
                </Button>
              )}
            </div>
            {mode && (
              <div className="mt-3 space-y-2">
                <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="work-text-input"
                  placeholder={mode === "blocker" ? "What's stopping the work, and what do you need?" : "Completion notes (optional)"} />
                <Button disabled={!!busy || (mode === "blocker" && !text.trim())} className={GOLD} data-testid="work-text-confirm"
                  onClick={() => mode === "blocker"
                    ? run("b", () => api.post(`/workspace/actions/${a.id}/blocker`, { text }), "Blocker raised — your manager has been told")
                    : run("s", () => api.post(`/workspace/actions/${a.id}/submit`, { notes: text || null }), a.requires_review ? "Submitted for review" : "Marked complete")}>
                  {busy ? <Icons.Loader2 className="animate-spin" size={16} /> : "Confirm"}
                </Button>
              </div>
            )}
          </div>
        )}

        {awaiting && !iAmReviewer && (
          <div className="rounded-lg bg-[#F7F9FC] px-3.5 py-3 text-sm text-[#1E2D44]/70">Submitted {fmtDate(a.submitted_at)} — waiting for {a.reviewer_name || "the reviewer"} to check it.</div>
        )}
        {iAmReviewer && (
          <div className="rounded-xl border border-[#FFC24D] bg-[#FFB000]/5 p-4" data-testid="review-box">
            <div className="text-sm font-semibold text-[#0B1729]">Your independent review</div>
            {a.completion_notes && <p className="mt-1 text-sm text-[#1E2D44]/70">“{a.completion_notes}”</p>}
            <Textarea value={text} onChange={(e) => setText(e.target.value)} className="mt-3 bg-white" placeholder="Notes (required if returning)" data-testid="review-notes" />
            <div className="mt-3 flex gap-2">
              <Button className={GOLD} disabled={!!busy} data-testid="approve-work-btn"
                onClick={() => run("r", () => api.post(`/workspace/actions/${a.id}/review`, { decision: "approve", notes: text || null }), "Approved")}>
                <Icons.Check size={15} className="mr-1.5" /> Approve
              </Button>
              <Button variant="outline" disabled={!!busy || !text.trim()} data-testid="return-work-btn"
                onClick={() => run("r", () => api.post(`/workspace/actions/${a.id}/review`, { decision: "return", notes: text }), "Returned with comments")}>
                <Icons.Undo2 size={15} className="mr-1.5" /> Return
              </Button>
            </div>
          </div>
        )}

        <EvidencePanel a={a} canEdit={doer && !closed} onChanged={() => { load(); onChanged?.(); }} />
        <HistoryTimeline events={a.history} />
      </DialogContent>
    </Dialog>
  );
}
